import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IBillingAddress, IBusiness, IBusinessResponse, IInvoice, IInvoiceResponse, IResponseMessage, IUsersRespone, IBusinessSetting } from '../../models';
import { HttpClient } from '@angular/common/http';
import { ApiConstants } from '../../common';

@Injectable({
  providedIn: 'root',
})
export class BusinessService {

  constructor(private http: HttpClient) {

  }

  //API TO FETCH ALL BUSINESS
  getAllBusiness(): Observable<IBusinessResponse> {
    return this.http.get<IBusinessResponse>(ApiConstants.superAdminBusiness);
  }

  //API TO FETCH BUSINESS BY PAGINATION
  getAllBusinessByLimit(limit: number, offset: number): Observable<IBusinessResponse> {
    const url = ApiConstants.superAdminBusiness + '?limit=' + limit + '&offset=' + offset;
    return this.http.get<IBusinessResponse>(url);
  }

  //API TO FETCH BUSINESS DETAIL BY ID
  getBusinessById(business_id: number): Observable<IBusiness> {
    return this.http.get<IBusiness>(ApiConstants.superAdminBusiness + business_id + '/');
  }

  //API TO GET ALL CUSTOM INVOICE BY BUSINESS
  getAllCustomInvoicesByBusiness(business_id: number): Observable<IInvoiceResponse> {
    return this.http.get<IInvoiceResponse>(ApiConstants.superAdminCustomInvoice + `?business_id=${business_id}`);
  }

  //API TO GET ALL USERS BY BUSINESS
  getAllUsersByBusiness(business_id: number): Observable<IUsersRespone> {
    return this.http.get<IUsersRespone>(ApiConstants.superAdminBusinessUsers + `?business_id=${business_id}`);
  }

  //API TO GENERATE NEW CUSTOM INVOICE
  generateCustomInvoice(invoice: IInvoice): Observable<IInvoice> {
    return this.http.post<IInvoice>(ApiConstants.superAdminCustomInvoice, invoice);
  }

  //API TO UPDATE EXISTING CUSTOM INVOICE
  updateCustomInvoice(invoice: IInvoice): Observable<IInvoice> {
    return this.http.put<IInvoice>(ApiConstants.superAdminCustomInvoice + `${invoice.id}/`, invoice);
  }

  //API TO DOWNLOAD INVOICE
  downloadCustomInvoice(invoice: IInvoice) {
    const invoice_id = invoice.id;
    const business_id = invoice.business;
    return this.http.get(ApiConstants.superAdminDownloadInvoice + `?business_id=${business_id}&invoice_id=${invoice_id}`, {
      responseType: 'blob'
    });
  }

  //API TO DELETE EXISTING CUSTOM INVOICE
  deleteCustomInvoice(invoice: IInvoice): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(ApiConstants.superAdminCustomInvoice + `${invoice.id}/`);
  }

  //API TO GET BILLING ADDRESS BY BUSINESS
  getBillingAddressByBusiness(business_id: number): Observable<IBillingAddress[]> {
    return this.http.get<IBillingAddress[]>(ApiConstants.superAdminBillingAddress + `?business_id=${business_id}`);
  }

  //API TO ADD BILLING ADDRESS BY BUSINESS
  addBillingAddressByBusiness(billing_address: IBillingAddress, business_id: number, user_id: number): Observable<IBillingAddress> {
    return this.http.post<IBillingAddress>(ApiConstants.superAdminBillingAddress + `?business_id=${business_id}&user_id=${user_id}`, billing_address);
  }

  //API TO UPDATE BILLING ADDRESS BY BUSINESS
  updateBillingAddressByBusiness(billing_address: IBillingAddress): Observable<IResponseMessage> {
    return this.http.put<IResponseMessage>(ApiConstants.superAdminBillingAddress + `${billing_address.id}/`, billing_address);
  }

  //API TO UPDATE BUSINESS DETAIL
  updateBusinessDetails(business: IBusiness): Observable<IBusiness> {
    return this.http.put<IBusiness>(ApiConstants.superAdminBusinessEdit + `${business.business}/`, business);
  }

  //API TO UPDATE BUSINESS NAME
  updateBusinessName(business_id: number, name: string): Observable<IBusiness> {
    return this.http.patch<IBusiness>(ApiConstants.superAdminBusiness + `${business_id}/`, { name });
  }

  //API TO MARK EXISTING CUSTOM INVOICE PAYMENT AS DONE
  markInvoicePaymentDone(invoice: IInvoice): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(ApiConstants.superAdminCustomInvoice + `${invoice.id}/update-status/`,
      {
        is_payment_done: true
      }
    );
  }

  //API TO CANCEL OR MAKE THE INVOICE VOID
  cancelInvoice(invoice: IInvoice): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(ApiConstants.superAdminCustomInvoice + `${invoice.id}/update-status/`,
      {
        is_void: true
      }
    );
  }

  //API TO SEND COPY OF INVOICE TO THE BUSINESSS ADMIN
  sendInvoiceCopy(invoice: IInvoice): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(ApiConstants.superAdminSendInvoiceCopy,
      {
        business_id: invoice.business,
        invoice_id: invoice.id
      }
    );
  }

  //API TO SEND COPY OF INVOICE TO THE BUSINESSS ADMIN
  sendReceiptMail(invoice: IInvoice): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(ApiConstants.superAdminSendReceiptMail,
      {
        invoice_id: invoice.id
      }
    );
  }

  //API TO FETCH BUSINESS SETTINGS
  getBusinessSettings(business_id: number): Observable<IBusinessSetting | IBusinessSetting[] | any> {
    return this.http.get<IBusinessSetting | IBusinessSetting[] | any>(ApiConstants.superAdminBusinessSettings + `?business_id=${business_id}`);
  }

  //API TO UPDATE BUSINESS SETTINGS (WATERMARK, LAUNCHER, ETC.)
  updateBusinessSettings(payload: {
    business_id: number;
    is_watermark_enabled?: boolean;
    is_launcher_available?: boolean;
  }): Observable<IBusinessSetting | { message: string }> {
    return this.http.post<IBusinessSetting | { message: string }>(ApiConstants.superAdminBusinessSettings, payload);
  }

  //API TO ENABLE OR DISABLE WATERMARK OF ZIGNFLIX IN THE DIGITAL SIGNAGE APPLICATION
  updateWatermarkSetting(business_id: number, is_watermark_enabled: boolean, is_launcher_available?: boolean): Observable<IBusinessSetting | { message: string }> {
    const payload: { business_id: number; is_watermark_enabled: boolean; is_launcher_available?: boolean } = {
      business_id: business_id,
      is_watermark_enabled: is_watermark_enabled
    };
    if (is_launcher_available !== undefined) {
      payload.is_launcher_available = is_launcher_available;
    }
    return this.updateBusinessSettings(payload);
  }

  //API TO ENABLE OR DISABLE LAUNCHER IN THE DIGITAL SIGNAGE APPLICATION
  updateLauncherSetting(business_id: number, is_launcher_available: boolean, is_watermark_enabled?: boolean): Observable<IBusinessSetting | { message: string }> {
    const payload: { business_id: number; is_launcher_available: boolean; is_watermark_enabled?: boolean } = {
      business_id: business_id,
      is_launcher_available: is_launcher_available
    };
    if (is_watermark_enabled !== undefined) {
      payload.is_watermark_enabled = is_watermark_enabled;
    }
    return this.updateBusinessSettings(payload);
  }

  //API TO DELETE BUSINESS BY ID
  deleteBusinessById(business_id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(ApiConstants.superAdminBusinessDelete + `${business_id}/`);
  }

}
